<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import WhatsappChannel from 'dashboard/api/channel/whatsappChannel';

const { t } = useI18n();

const props = defineProps({
  mockData: {
    type: Object,
    default: null,
  },
  mockError: {
    type: String,
    default: null,
  },
  mockLoading: {
    type: Boolean,
    default: undefined,
  },
});

const limitsData = ref(props.mockData || null);
const isLoading = ref(
  props.mockLoading !== undefined
    ? props.mockLoading
    : !props.mockData && !props.mockError
);
const error = ref(props.mockError || null);

const isExpanded = ref(false);

const fetchLimits = async () => {
  // Se estivermos usando mocks, não fazemos o fetch real, 
  // exceto se for explicitamente solicitado via refresh (neste caso, o reload faz sentido)
  // Mas para o story, o refresh pode falhar ou ser ignorado. 
  // Vamos permitir o fetch se chamado manualmente, mas o setup inicial respeita os mocks.
  
  isLoading.value = true;
  error.value = null;
  
  try {
    const response = await WhatsappChannel.getTemplateMessageLimits();
    limitsData.value = response.data;
  } catch (err) {
    error.value = err.message || t('CAMPAIGN.WHATSAPP.LIMITS.ERROR_LOADING');
  } finally {
    isLoading.value = false;
  }
};

const totalPercentage = computed(() => {
  if (!limitsData.value) return 0;
  return Math.round(
    (limitsData.value.usage.total / limitsData.value.limits.total) * 100
  );
});

const superAdminPercentage = computed(() => {
  if (!limitsData.value) return 0;
  return Math.round(
    (limitsData.value.usage.super_admin / limitsData.value.limits.super_admin) * 100
  );
});

const managerPercentage = computed(() => {
  if (!limitsData.value) return 0;
  return Math.round(
    (limitsData.value.usage.manager / limitsData.value.limits.manager) * 100
  );
});

const getStatusClass = percentage => {
  if (percentage >= 90) return 'bg-red-500';
  if (percentage >= 70) return 'bg-yellow-400';
  return 'bg-woot-500';
};

const getStatusTextClass = percentage => {
  if (percentage >= 90) return 'text-red-500';
  if (percentage >= 70) return 'text-yellow-400';
  return 'text-woot-500';
};

const formatResetDate = dateString => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit'
  });
};

onMounted(() => {
  if (!props.mockData && !props.mockError && props.mockLoading === undefined) {
    fetchLimits();
  }
});

const toggleExpanded = () => {
  isExpanded.value = !isExpanded.value;
  console.log('WhatsAppTemplateLimits expanded:', isExpanded.value);
};

// Expor método para componente pai poder atualizar
defineExpose({
  fetchLimits
});
</script>

<template>
  <div class="bg-n-white dark:bg-n-slate-2 rounded-lg border border-n-weak mb-4 overflow-hidden">
    <div
      class="flex items-center justify-between p-5 cursor-pointer select-none transition-colors hover:bg-n-alpha-1"
      @click="isExpanded = !isExpanded"
    >
      <div class="flex items-center gap-3">
        <span
          class="i-lucide-chevron-down text-n-slate-11 transition-transform duration-300"
          :class="{ '-rotate-90': !isExpanded }"
        />
        <h3 class="text-base font-medium text-n-slate-12 flex items-center gap-2">
          <span class="i-lucide-bar-chart-2 text-lg" />
          {{ t('CAMPAIGN.WHATSAPP.LIMITS.TITLE') }}
        </h3>
      </div>
      <button
        type="button"
        class="text-sm text-n-slate-11 hover:text-n-slate-12 flex items-center gap-1 z-10 p-1.5 rounded-md hover:bg-n-alpha-2"
        :aria-label="t('CAMPAIGN.WHATSAPP.LIMITS.REFRESH')"
        @click.stop="fetchLimits"
      >
        <span class="i-lucide-refresh-cw text-base" />
      </button>
    </div>

    <!-- Expandable Body -->
    <div
      class="grid w-full"
      :style="{ 
        'grid-template-rows': isExpanded ? '1fr' : '0fr',
        'transition': 'grid-template-rows 300ms ease-in-out'
      }"
    >
      <div class="overflow-hidden">
        <div class="px-5 pb-5 pt-0">
          <!-- Loading State -->
          <div v-if="isLoading" class="flex items-center gap-2 text-n-slate-11 text-sm py-2">
            <span class="i-lucide-loader-2 animate-spin text-base" />
            <span>{{ t('CAMPAIGN.WHATSAPP.LIMITS.LOADING') }}</span>
          </div>

          <!-- Error State -->
          <div
            v-else-if="error"
            class="bg-n-red-2 border border-n-red-6 rounded-lg p-4 text-n-red-11"
          >
            <div class="flex items-center gap-2">
              <span class="i-lucide-alert-circle text-lg" />
              <span>{{ error }}</span>
            </div>
          </div>

          <!-- Limits Data -->
          <div v-else-if="limitsData" class="space-y-4 pt-2">
            <!-- Super Admin Limit -->
            <div class="space-y-2">
              <div class="flex items-center justify-between text-sm">
                <div class="flex items-center gap-2">
                  <span class="i-lucide-crown text-base text-n-slate-10" />
                  <span class="text-n-slate-11">
                    {{ t('CAMPAIGN.WHATSAPP.LIMITS.SUPER_ADMIN_LABEL') }}
                  </span>
                </div>
                <span
                  class="font-medium"
                  :class="getStatusTextClass(superAdminPercentage)"
                >
                  {{ limitsData.usage.super_admin }} /
                  {{ limitsData.limits.super_admin }}
                  <span class="text-n-slate-10">({{ superAdminPercentage }}%)</span>
                </span>
              </div>
              <div class="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 overflow-hidden">
                <div
                  class="h-full transition-all duration-300 rounded-full"
                  :class="getStatusClass(superAdminPercentage)"
                  :style="{ width: `${superAdminPercentage}%` }"
                />
              </div>
              <div class="flex items-center justify-between text-xs">
                <span class="text-n-slate-10">
                  {{ t('CAMPAIGN.WHATSAPP.LIMITS.REMAINING') }}:
                  <span class="font-medium text-n-slate-11">
                    {{ limitsData.remaining.super_admin }}
                  </span>
                </span>
              </div>
            </div>

            <!-- Manager Limit -->
            <div class="space-y-2">
              <div class="flex items-center justify-between text-sm">
                <div class="flex items-center gap-2">
                  <span class="i-lucide-shield text-base text-n-slate-10" />
                  <span class="text-n-slate-11">
                    {{ t('CAMPAIGN.WHATSAPP.LIMITS.MANAGER_LABEL') }}
                  </span>
                </div>
                <span
                  class="font-medium"
                  :class="getStatusTextClass(managerPercentage)"
                >
                  {{ limitsData.usage.manager }} /
                  {{ limitsData.limits.manager }}
                  <span class="text-n-slate-10">({{ managerPercentage }}%)</span>
                </span>
              </div>
              <div class="w-full bg-slate-200 dark:bg-slate-700 rounded-full h-3 overflow-hidden">
                <div
                  class="h-full transition-all duration-300 rounded-full"
                  :class="getStatusClass(managerPercentage)"
                  :style="{ width: `${managerPercentage}%` }"
                />
              </div>
              <div class="flex items-center justify-between text-xs">
                <span class="text-n-slate-10">
                  {{ t('CAMPAIGN.WHATSAPP.LIMITS.REMAINING') }}:
                  <span class="font-medium text-n-slate-11">
                    {{ limitsData.remaining.manager }}
                  </span>
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

